import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { toast } from "sonner";
import { ShieldCheck, Activity, Ban, Plus, Radio, Database, RefreshCw } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const INTEG_STYLES = {
  mocked: "text-sky-400",
  manual: "text-amber-400",
  not_wired: "text-slate-500",
  ok: "text-emerald-400",
  live_read_only: "text-emerald-300",
  configured_but_disabled: "text-amber-400",
};

const INTEG_LABEL = {
  live_read_only: "LIVE READ-ONLY",
  configured_but_disabled: "CONFIGURED · DISABLED",
};

export default function Settings() {
  const [kind, setKind] = useState("domain");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings/overview")).data,
  });

  const { data: atStatus } = useQuery({
    queryKey: ["airtable-status"],
    queryFn: async () => (await api.get("/airtable/status")).data,
  });

  const { data: atRecon, refetch: refetchRecon, isFetching: reconFetching } = useQuery({
    queryKey: ["airtable-reconciliation"],
    queryFn: async () => (await api.get("/airtable/reconciliation")).data,
    enabled: !!atStatus?.ready,
  });

  const addSuppression = useMutation({
    mutationFn: async () => (await api.post("/settings/suppression", { kind, value, reason })).data,
    onSuccess: () => {
      toast.success("Suppression entry added (writes to authoritative production store)");
      setValue(""); setReason("");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to add suppression"),
  });

  if (isLoading) return <div className="text-slate-500 font-mono text-sm" data-testid="settings-loading">Loading settings…</div>;
  const d = data || { campaigns: [], suppression: [], health: { integrations: [], atlas: {} }, telemetry: { per_day: [], total_calls: 0, daily_soft_cap: 500 }, audit: {} };
  const todayCalls = d.telemetry.per_day?.[d.telemetry.per_day.length - 1]?.calls || 0;
  const capPct = Math.min((todayCalls / d.telemetry.daily_soft_cap) * 100, 100);

  return (
    <div className="space-y-6" data-testid="settings-view">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100">Settings & Governance</h1>
        <p className="text-sm text-slate-400 mt-1">Suppression, campaigns, integration health, ATLAS contract and Airtable telemetry.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="suppression-panel">
          <div className="flex items-center gap-2">
            <Ban size={14} className="text-rose-400" />
            <h2 className="text-lg font-medium text-slate-200 tracking-tight">Suppression List</h2>
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 ml-auto">authoritative: production store</span>
          </div>
          <div className="flex gap-2">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              data-testid="suppression-kind-select"
              className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-200 focus:outline-none"
            >
              <option value="domain">Domain</option>
              <option value="email">Email</option>
              <option value="company">Company</option>
            </select>
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="value"
              data-testid="suppression-value-input"
              className="flex-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-500 font-mono"
            />
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="reason"
              data-testid="suppression-reason-input"
              className="flex-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
            <button
              onClick={() => addSuppression.mutate()}
              disabled={addSuppression.isPending || !value.trim()}
              data-testid="suppression-add-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border bg-[#7F1D1D] text-[#F87171] border-[#DC2626] hover:brightness-125 transition-all disabled:opacity-40"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {d.suppression.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-3 py-1.5 rounded border border-slate-800/60 bg-slate-950/40" data-testid={`suppression-entry-${s.id}`}>
                <span className="font-mono text-xs text-slate-300">{s.value}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{s.kind} · {s.reason || "no reason"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="integration-health-panel">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-sky-400" />
              <h2 className="text-lg font-medium text-slate-200 tracking-tight">Integration Health</h2>
            </div>
            <div className="space-y-2">
              {d.health.integrations?.map((i) => (
                <div key={i.name} className="flex items-start justify-between gap-3" data-testid={`integration-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div>
                    <div className="text-sm text-slate-300">{i.name}</div>
                    <div className="text-[11px] text-slate-500">{i.detail}</div>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-wider shrink-0 ${INTEG_STYLES[i.status] || "text-slate-400"}`}>
                    {INTEG_LABEL[i.status] || i.status.replace(/_/g, " ")}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="atlas-contract-panel">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-emerald-400" />
              <h2 className="text-lg font-medium text-slate-200 tracking-tight">ATLAS Contract</h2>
            </div>
            <div className="font-mono text-xs text-slate-400 space-y-1">
              <div>version: <span className="text-emerald-300">{d.health.atlas?.contract_version}</span></div>
              <div>bearer auth: <span className="text-emerald-300">{d.health.atlas?.bearer_configured ? "configured" : "missing"}</span></div>
              <div>consumer: <span className="text-slate-300">{d.health.atlas?.consumer}</span></div>
              <div className="pt-1 text-slate-500">GET /api/v1/atlas/summary · /attention · /delta</div>
              <div className="text-slate-500">webhooks (stubbed): event.material_reply · event.approval_required · event.integration_failed · event.outcome_recorded</div>
            </div>
            <div className="pt-1 border-t border-slate-800/60 font-mono text-[11px] text-slate-500" data-testid="audit-log-summary">
              Immutable decision log: <span className="text-sky-300">{d.audit.decisions_logged}</span> decisions · {d.audit.store}
            </div>
          </div>
        </div>
      </div>

      <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="airtable-panel">
        <div className="flex items-center gap-2">
          <Database size={14} className="text-emerald-400" />
          <h2 className="text-lg font-medium text-slate-200 tracking-tight">Airtable Adapter · Read-only</h2>
          <span className={`ml-auto font-mono text-[10px] uppercase tracking-widest shrink-0 ${
            atStatus?.ready ? "text-emerald-300"
              : atStatus?.pat_configured ? "text-amber-400"
              : "text-slate-500"
          }`} data-testid="airtable-adapter-status">
            {atStatus?.ready ? "LIVE READ-ONLY" : atStatus?.pat_configured ? "CONFIGURED · DISABLED" : "NOT CONFIGURED"}
          </span>
        </div>
        <div className="font-mono text-[11px] text-slate-400 space-y-1">
          <div>data source mode: <span className="text-slate-200">{atStatus?.data_source_mode || "—"}</span></div>
          <div>base id: <span className="text-slate-200">{atStatus?.base_id || "—"}</span></div>
          <div>cache ttl: <span className="text-slate-200">{atStatus?.cache_ttl_seconds ?? "—"}s</span></div>
          <div>allow-listed tables: <span className="text-slate-200">{atStatus?.allow_listed_tables ? Object.keys(atStatus.allow_listed_tables).length : 0}</span></div>
          <div className="text-slate-500 pt-1">{atStatus?.note}</div>
        </div>
        {atStatus?.ready ? (
          <>
            <div className="flex items-center justify-between pt-2 border-t border-slate-800/60">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Reconciliation snapshot</span>
              <button
                onClick={() => refetchRecon()}
                disabled={reconFetching}
                data-testid="airtable-reconcile-refresh-btn"
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700 disabled:opacity-40"
              >
                <RefreshCw size={11} className={reconFetching ? "animate-spin" : ""} /> Refresh
              </button>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {atRecon?.tables && Object.entries(atRecon.tables).map(([alias, r]) => (
                <div key={alias} className="flex items-center justify-between px-3 py-1.5 rounded border border-slate-800/60 bg-slate-950/40" data-testid={`airtable-table-${alias}`}>
                  <div>
                    <div className="font-mono text-xs text-slate-200">{alias}</div>
                    <div className="font-mono text-[10px] text-slate-500">{r.table_id}{r.sample_ids ? ` · sample: ${r.sample_ids.join(", ")}` : ""}</div>
                  </div>
                  <span className="font-mono text-xs text-emerald-300 shrink-0">
                    {r.error ? <span className="text-rose-300">{r.error}</span> : `${r.count} records`}
                  </span>
                </div>
              ))}
            </div>
            {atRecon && (
              <div className="pt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500 flex justify-between">
                <span>API calls (this base): {atRecon.airtable_api_calls_total}</span>
                <span className={atRecon.airtable_api_errors_total ? "text-rose-300" : ""}>errors: {atRecon.airtable_api_errors_total}</span>
              </div>
            )}
          </>
        ) : (
          <div className="text-[11px] text-slate-500 border-t border-slate-800/60 pt-2">
            To activate: paste a scoped read-only PAT (starts with <span className="font-mono text-slate-300">pat...</span>) — once stored in <span className="font-mono">/app/backend/.env</span> as <span className="font-mono text-slate-300">AIRTABLE_PAT</span> with <span className="font-mono text-slate-300">DATA_SOURCE_MODE=airtable_readonly</span>, the backend will hot-restart and this panel will flip to <span className="text-emerald-300 font-mono">LIVE READ-ONLY</span>.
          </div>
        )}
      </div>


      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="telemetry-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio size={14} className="text-sky-400" />
              <h2 className="text-lg font-medium text-slate-200 tracking-tight">Adapter API Telemetry</h2>
            </div>
            <span className="font-mono text-xs text-slate-400" data-testid="telemetry-total">{d.telemetry.total_calls} app-attributable calls</span>
          </div>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.telemetry.per_day} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", fontSize: 12 }} labelStyle={{ color: "#94A3B8" }} />
                <Area type="monotone" dataKey="calls" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.15} strokeWidth={1.5} />
                <Area type="monotone" dataKey="errors" stroke="#EF4444" fill="#EF4444" fillOpacity={0.2} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <span>today: {todayCalls} calls</span>
              <span>soft cap: {d.telemetry.daily_soft_cap}/day</span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden" data-testid="telemetry-quota-gauge">
              <div className="h-full bg-sky-500 rounded-full transition-all duration-500" style={{ width: `${capPct}%` }} />
            </div>
          </div>
        </div>

        <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="campaigns-panel">
          <h2 className="text-lg font-medium text-slate-200 tracking-tight">Campaigns</h2>
          <div className="space-y-2">
            {d.campaigns.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-800/60 bg-slate-950/40" data-testid={`campaign-${c.id}`}>
                <div>
                  <div className="text-sm text-slate-200">{c.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{c.channel}</div>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-wider ${c.status === "active" ? "text-emerald-400" : "text-slate-500"}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
